/**
 * Vue admin Sentry wiring — thin wrapper around `@gencay-akademi/sentry-config/admin`.
 *
 * Activates only when VITE_SENTRY_DSN is set. With no DSN this is a no-op
 * (no SDK calls, zero runtime cost).
 *
 * Wire from main.ts AFTER the router is created and BEFORE app.mount():
 *
 *   import { initSentry } from './config/sentry'
 *   initSentry(app, router)
 */

import {
  clearAdminSentryUser,
  initAdminSentry,
  setAdminSentryUser,
} from '@gencay-akademi/sentry-config/admin'
import type { App } from 'vue'
import type { Router } from 'vue-router'

export function initSentry(app: App, router: Router): void {
  const dsn = import.meta.env['VITE_SENTRY_DSN']
  if (!dsn) {
    if (import.meta.env.DEV) {
      console.info('[sentry] disabled — VITE_SENTRY_DSN not set')
    }
    return
  }

  initAdminSentry({
    app,
    router,
    config: {
      dsn,
      environment:
        (import.meta.env['VITE_NODE_ENV'] as string | undefined) ??
        import.meta.env.MODE ??
        'production',
      release: (import.meta.env['VITE_APP_VERSION'] as string | undefined) ?? 'unknown',
      tracesSampleRate: Number(import.meta.env['VITE_SENTRY_TRACES_SAMPLE_RATE'] ?? '0.1'),
      replaysSessionSampleRate: Number(
        import.meta.env['VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE'] ?? '0'
      ),
      replaysOnErrorSampleRate: Number(
        import.meta.env['VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE'] ?? '1.0'
      ),
      sendDefaultPii: false,
    },
  })
}

/** Set the user context (call after login). */
export function setSentryUser(userId: string, email?: string, username?: string): void {
  if (!import.meta.env['VITE_SENTRY_DSN']) return
  setAdminSentryUser(userId, email, username)
}

/** Clear user context (call on logout). */
export function clearSentryUser(): void {
  if (!import.meta.env['VITE_SENTRY_DSN']) return
  clearAdminSentryUser()
}
